import { ArrowDownLeft, ArrowUpRight, Building2, Scale, Target, TrendingUp } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useProjects } from '../projects/api'
import { fmtMoney } from '../shared/format'
import { useParties } from './api'
import { BankSelect } from './components/banks/BankSelect'
import { IncomeExpenseChart } from './components/Charts'
import { ExportMenu } from './components/ExportMenu'
import { Kpi, PeriodSelect, Section, TrendChip } from './components/FinanceBits'
import { BUDGET_PERMS } from './budgets'
import { DEPARTMENT_NONE, useFinanceDepartments, usePnLDepartments } from './departments'
import { exportPnLCsv, usePnL, type PnLBasis, type PnLLine, type PnLReport } from './pnl'
import { downloadReport } from './reports'
import { monthShort } from './ui'

const thisMonth = () => new Date().toISOString().slice(0, 7)
const yearStart = () => `${new Date().getFullYear()}-01`

/** Önceki aralığa göre değişim (yüzde); önceki sıfırsa karşılaştırma yok. */
const change = (cur: string, prev: string) => {
  const c = Number(cur)
  const p = Number(prev)
  return p ? Math.round(((c - p) / Math.abs(p)) * 1000) / 10 : null
}

/**
 * Kâr/zarar ve aylık maliyet: tahakkuk (yevmiye) ya da nakit (tahsilat/ödeme) bazında aylık gelir, gider, sabit/değişken
 * gider, personel maliyeti ve net kâr; proje, cari ve (nakit bazında) banka filtresi; önceki aynı uzunluktaki aralıkla kıyas.
 */
export function PnLPage() {
  const { t } = useTranslation()
  const [from, setFrom] = useState(yearStart)
  const [to, setTo] = useState(thisMonth)
  const [basis, setBasis] = useState<PnLBasis>('accrual')
  const [projectId, setProjectId] = useState('')
  const [partyId, setPartyId] = useState('')
  const [bankId, setBankId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const { can } = useAuth()
  const canBudget = can(BUDGET_PERMS.read)
  const [withBudget, setWithBudget] = useState(() => canBudget && new URLSearchParams(window.location.search).get('budget') === '1')
  const q = usePnL({ from, to, basis, projectId, partyId, bankId, departmentId, withBudget })
  const departments = useFinanceDepartments()
  const breakdown = usePnLDepartments({ from, to, projectId, partyId }, basis === 'accrual' && !departmentId && !!departments.data?.length)
  const projects = useProjects()
  const parties = useParties()
  const rep = q.data
  const all = { value: '', label: t('finance.pnl.all') }
  const params = {
    from,
    to,
    basis,
    project_id: projectId,
    party_id: partyId,
    bank_id: basis === 'cash' ? bankId : '',
    department_id: departmentId,
    budget: withBudget && basis === 'accrual' ? '1' : '',
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.pnl.title')}
        description={t('finance.pnl.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodSelect value={from} onChange={setFrom} />
            <span className="text-muted-foreground text-sm">–</span>
            <PeriodSelect value={to} onChange={setTo} />
            <ExportMenu
              disabled={!rep || (!rep.income.length && !rep.expense.length)}
              onExcel={() => downloadReport('pnl', 'xlsx', params)}
              onCorporatePdf={() => downloadReport('pnl', 'pdf', params)}
              onCsv={() =>
                rep &&
                exportPnLCsv(rep, {
                  line: t('finance.pnl.line'),
                  total: t('finance.pnl.total'),
                  income: t('finance.pnl.incomeTotal'),
                  expense: t('finance.pnl.expenseTotal'),
                  net: t('finance.pnl.net'),
                  fixed: t('finance.pnl.fixed'),
                  masked: t('finance.pnl.masked'),
                })
              }
            />
          </div>
        }
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={basis} onValueChange={(v) => setBasis(v as PnLBasis)}>
            <TabsList size="sm">
              <TabsTrigger value="accrual">{t('finance.pnl.basis.accrual')}</TabsTrigger>
              <TabsTrigger value="cash">{t('finance.pnl.basis.cash')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select
            className="w-56"
            aria-label={t('finance.pnl.project')}
            searchable
            value={projectId}
            onChange={setProjectId}
            options={[{ ...all, label: t('finance.pnl.allProjects') }, ...(projects.data ?? []).map((p) => ({ value: p.id, label: p.name }))]}
          />
          <Select
            className="w-56"
            aria-label={t('finance.pnl.party')}
            searchable
            value={partyId}
            onChange={setPartyId}
            options={[{ ...all, label: t('finance.pnl.allParties') }, ...(parties.data ?? []).map((p) => ({ value: p.id, label: p.name }))]}
          />
          {departments.data?.length ? (
            <Select
              className="w-52"
              aria-label={t('finance.departments.field')}
              searchable
              value={departmentId}
              onChange={setDepartmentId}
              options={[
                { value: '', label: t('finance.departments.all') },
                { value: DEPARTMENT_NONE, label: t('finance.departments.none') },
                ...departments.data.map((d) => ({ value: d.id, label: d.name })),
              ]}
            />
          ) : null}
          {canBudget && basis === 'accrual' ? (
            <label className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm">
              <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={withBudget} onChange={(e) => setWithBudget(e.target.checked)} />
              {t('finance.budgets.compare')}
            </label>
          ) : null}
          {basis === 'cash' ? (
            <div className="w-52">
              <BankSelect id="pnl-bank" value={bankId} onChange={setBankId} emptyLabel={t('finance.bankReport.allBanks')} />
            </div>
          ) : null}
        </div>
        <p className="text-muted-foreground text-xs">{t(`finance.pnl.basisHint.${basis}`)}</p>
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : !rep ? (
          <Skeleton className="h-64" />
        ) : (
          <Report rep={rep} />
        )}
        {breakdown.data && breakdown.data.length > 1 ? <DepartmentBreakdown rows={breakdown.data} /> : null}
      </div>
    </>
  )
}

/** Departman kırılımı (tahakkuk): departmanlar toplamı filtresiz kâr/zararla aynıdır; departmansız kayıtlar ayrı satır. */
function DepartmentBreakdown({ rows }: { rows: ReturnType<typeof usePnLDepartments>['data'] & object }) {
  const { t } = useTranslation()
  const personnel = rows.some((r) => r.personnel != null)
  const sum = (k: 'income' | 'expense' | 'net' | 'personnel') => rows.reduce((n, r) => n + Number(r[k] ?? 0), 0).toFixed(2)
  return (
    <Section title={t('finance.departments.breakdown')} icon={Building2} bodyClassName="p-0">
      <div className="overflow-x-auto">
        <Table>
          <THead>
            <TR>
              <TH>{t('finance.departments.field')}</TH>
              <TH className="text-right">{t('finance.pnl.income')}</TH>
              <TH className="text-right">{t('finance.pnl.expense')}</TH>
              {personnel ? <TH className="text-right">{t('finance.pnl.personnel')}</TH> : null}
              <TH className="text-right">{t('finance.pnl.net')}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR key={r.departmentId || 'none'}>
                <TD className={cn(!r.departmentId && 'text-muted-foreground italic')}>{r.departmentId ? r.name : t('finance.departments.none')}</TD>
                <TD className="text-right tabular-nums">{fmtMoney(r.income)}</TD>
                <TD className="text-right tabular-nums">{fmtMoney(r.expense)}</TD>
                {personnel ? <TD className="text-right tabular-nums">{fmtMoney(r.personnel ?? '0')}</TD> : null}
                <TD className={cn('text-right tabular-nums', Number(r.net) < 0 && 'text-destructive')}>{fmtMoney(r.net)}</TD>
              </TR>
            ))}
            <TR className="border-t-2 font-semibold">
              <TD>{t('finance.reports.totals')}</TD>
              <TD className="text-right tabular-nums">{fmtMoney(sum('income'))}</TD>
              <TD className="text-right tabular-nums">{fmtMoney(sum('expense'))}</TD>
              {personnel ? <TD className="text-right tabular-nums">{fmtMoney(sum('personnel'))}</TD> : null}
              <TD className="text-right tabular-nums">{fmtMoney(sum('net'))}</TD>
            </TR>
          </TBody>
        </Table>
      </div>
    </Section>
  )
}

function Report({ rep }: { rep: PnLReport }) {
  const { t } = useTranslation()
  const tot = rep.totals
  const prev = rep.previous
  const vs = t('finance.pnl.vsPrevious', { from: monthShort(rep.previousFrom), to: monthShort(rep.previousTo) })
  return (
    <>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Kpi
          icon={ArrowDownLeft}
          tone="success"
          label={t('finance.pnl.incomeTotal')}
          value={fmtMoney(tot.income)}
          footer={<TrendChip trend={{ changePct: change(tot.income, prev.income) }} label={vs} />}
        />
        <Kpi
          icon={ArrowUpRight}
          tone="danger"
          label={t('finance.pnl.expenseTotal')}
          value={fmtMoney(tot.expense)}
          footer={
            rep.withBudget && tot.budgetExpense != null ? (
              <span className="inline-flex items-center gap-1 text-xs">
                <Target className="size-3.5" aria-hidden />
                {t('finance.budgets.kpi', { amount: fmtMoney(tot.budgetExpense) })}
              </span>
            ) : (
              <TrendChip trend={{ changePct: change(tot.expense, prev.expense) }} goodWhenUp={false} label={vs} />
            )
          }
        />
        <Kpi
          icon={TrendingUp}
          tone={Number(tot.net) < 0 ? 'danger' : 'info'}
          label={t('finance.pnl.net')}
          value={fmtMoney(tot.net)}
          footer={tot.marginPct == null ? vs : t('finance.pnl.margin', { pct: tot.marginPct.toLocaleString('tr-TR') })}
        />
        <Kpi
          icon={Scale}
          tone="neutral"
          label={t('finance.pnl.fixedVariable')}
          value={fmtMoney(tot.fixedExpense)}
          footer={
            t('finance.pnl.variableFooter', { amount: fmtMoney(tot.variableExpense) }) +
            (tot.personnel != null ? ` · ${t('finance.pnl.personnelFooter', { amount: fmtMoney(tot.personnel) })}` : '')
          }
        />
      </div>
      {rep.months.length > 1 ? (
        <Section title={t('finance.pnl.chart')} icon={TrendingUp}>
          <IncomeExpenseChart
            data={rep.months.map((m) => ({ month: m.period, income: m.income, expense: m.expense }))}
            incomeLabel={t('finance.pnl.income')}
            expenseLabel={t('finance.pnl.expense')}
          />
        </Section>
      ) : null}
      <Section title={t('finance.pnl.table')} icon={Scale} bodyClassName="p-0">
        {!rep.income.length && !rep.expense.length ? (
          <EmptyState icon={Scale} title={t('finance.pnl.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH className="bg-card sticky left-0 min-w-56">{t('finance.pnl.line')}</TH>
                  {rep.periods.map((p) => (
                    <TH key={p} className="text-right whitespace-nowrap">
                      {monthShort(p)}
                    </TH>
                  ))}
                  <TH className="text-right">{t('finance.pnl.total')}</TH>
                  {rep.withBudget ? (
                    <>
                      <TH className="text-right">{t('finance.budgets.budget')}</TH>
                      <TH className="text-right">{t('finance.budgets.variance')}</TH>
                    </>
                  ) : null}
                </TR>
              </THead>
              <TBody>
                <Group title={t('finance.pnl.income')} lines={rep.income} rep={rep} total="income" />
                <Group title={t('finance.pnl.expense')} lines={rep.expense} rep={rep} total="expense" />
                <TR className="border-t-2 font-semibold">
                  <TD className="bg-card sticky left-0">{t('finance.pnl.net')}</TD>
                  {rep.months.map((m) => (
                    <TD key={m.period} className={cn('text-right tabular-nums', Number(m.net) < 0 && 'text-destructive')}>
                      {fmtMoney(m.net)}
                    </TD>
                  ))}
                  <TD className={cn('text-right tabular-nums', Number(tot.net) < 0 && 'text-destructive')}>{fmtMoney(tot.net)}</TD>
                  {rep.withBudget ? (
                    <>
                      <TD className="text-right tabular-nums">{fmtMoney(tot.budgetNet ?? '0')}</TD>
                      <TD className="text-right tabular-nums">{fmtMoney((Number(tot.net) - Number(tot.budgetNet ?? 0)).toFixed(2))}</TD>
                    </>
                  ) : null}
                </TR>
                <TR className="text-muted-foreground text-xs">
                  <TD className="bg-card sticky left-0">{t('finance.pnl.marginRow')}</TD>
                  {rep.months.map((m) => (
                    <TD key={m.period} className="text-right tabular-nums">
                      {m.marginPct == null ? '—' : `%${m.marginPct.toLocaleString('tr-TR')}`}
                    </TD>
                  ))}
                  <TD className="text-right tabular-nums">{tot.marginPct == null ? '—' : `%${tot.marginPct.toLocaleString('tr-TR')}`}</TD>
                  {rep.withBudget ? <TD colSpan={2} /> : null}
                </TR>
              </TBody>
            </Table>
          </div>
        )}
      </Section>
    </>
  )
}

function Group({ title, lines, rep, total }: { title: string; lines: PnLLine[]; rep: PnLReport; total: 'income' | 'expense' }) {
  const { t } = useTranslation()
  const amount = (v: string | undefined) => (Number(v) ? fmtMoney(v ?? '0') : '—')
  return (
    <Fragment>
      <TR className="bg-muted/40">
        <TD colSpan={rep.periods.length + 2 + (rep.withBudget ? 2 : 0)} className="text-xs font-semibold uppercase tracking-wide">
          {title}
        </TD>
      </TR>
      {lines.map((l) => (
        <TR key={`${l.key}-${l.fixed}`}>
          <TD className="bg-card sticky left-0">
            <span className="flex flex-wrap items-center gap-1.5">
              {l.code && !l.code.startsWith('gl:') ? <span className="text-muted-foreground text-xs tabular-nums">{l.code}</span> : null}
              <span>{l.name || t('finance.pnl.uncategorized')}</span>
              {l.fixed ? <Badge tone="info">{t('finance.pnl.fixed')}</Badge> : null}
              {l.personnel ? <Badge tone="neutral">{t('finance.pnl.personnel')}</Badge> : null}
            </span>
          </TD>
          {l.masked ? (
            <TD colSpan={rep.periods.length + 1 + (rep.withBudget ? 2 : 0)} className="text-muted-foreground text-center text-xs">
              {t('finance.pnl.maskedHint')}
            </TD>
          ) : (
            <>
              {rep.periods.map((p) => (
                <TD key={p} className="text-right tabular-nums">
                  {amount(l.months[p])}
                </TD>
              ))}
              <TD className="text-right font-medium tabular-nums">{amount(l.total)}</TD>
              {rep.withBudget ? <BudgetCells line={l} /> : null}
            </>
          )}
        </TR>
      ))}
      <TR className="font-semibold">
        <TD className="bg-card sticky left-0">{total === 'income' ? t('finance.pnl.incomeTotal') : t('finance.pnl.expenseTotal')}</TD>
        {rep.months.map((m) => (
          <TD key={m.period} className="text-right tabular-nums">
            {fmtMoney(m[total])}
          </TD>
        ))}
        <TD className="text-right tabular-nums">{fmtMoney(rep.totals[total])}</TD>
        {rep.withBudget ? (
          <>
            <TD className="text-right tabular-nums">{fmtMoney((total === 'income' ? rep.totals.budgetIncome : rep.totals.budgetExpense) ?? '0')}</TD>
            <TD className="text-right tabular-nums">
              {fmtMoney((Number(rep.totals[total]) - Number((total === 'income' ? rep.totals.budgetIncome : rep.totals.budgetExpense) ?? 0)).toFixed(2))}
            </TD>
          </>
        ) : null}
      </TR>
    </Fragment>
  )
}

/** Bütçe ve fark; giderde aşım kırmızı, gelirde eksik kırmızı. */
function BudgetCells({ line }: { line: PnLLine }) {
  if (line.budgetTotal == null) {
    return (
      <>
        <TD className="text-right text-muted-foreground">—</TD>
        <TD className="text-right text-muted-foreground">—</TD>
      </>
    )
  }
  const v = Number(line.variance ?? 0)
  const bad = line.kind === 'expense' ? v > 0 : v < 0
  return (
    <>
      <TD className="text-right tabular-nums">{fmtMoney(line.budgetTotal)}</TD>
      <TD className={cn('text-right tabular-nums', bad ? 'text-destructive' : v !== 0 && 'text-success')}>
        {fmtMoney(line.variance ?? '0')}
        {line.variancePct != null ? <span className="ml-1 text-xs">(%{line.variancePct.toLocaleString('tr-TR')})</span> : null}
      </TD>
    </>
  )
}
